import { ApolloError } from '@apollo/client';
import { getOrderMetadata } from '@/screens/order-inbounds/utils';
import { ORDER_INBOUNDS_DETAIL_QUERY } from '@/screens/order-inbounds/queries';
import {
  OrderInbounds_DetailQuery,
  OrderInbounds_DetailQueryVariables,
  SmartOrderFieldsFragment,
} from '@/types';
import { OrderMetadata } from '@/screens/order-inbounds/types';
import { partnerSupplier } from '@/utils/environment';
import { useBaseSupplierItemQueryVariables } from './use-item-query-variables';
import { useMemo } from 'react';
import { useUserContext } from '@/contexts/user-context';
import { useWrappedQuery } from './use-wrapped-query';

interface UseOriginalInboundMetadataQueryInput {
  filterBySubmittedOrderUUID?: string;
  orderMetadata: { mergeUUID?: string; threadId?: string; uuid: string };
}

type UseOriginalInboundMetadataQueryOutput =
  | {
      data: null;
      error?: ApolloError;
      loading: true;
    }
  | {
      data: OrderMetadata[];
      error?: ApolloError;
      loading: false;
    };

export const useOriginalInboundMetadataQuery: (
  input: UseOriginalInboundMetadataQueryInput,
) => UseOriginalInboundMetadataQueryOutput = ({
  filterBySubmittedOrderUUID,
  orderMetadata,
}) => {
  const { selectedRestaurantUUID } = useUserContext();
  const itemQueryVariables = useBaseSupplierItemQueryVariables();

  const { data, loading, error } = useWrappedQuery<
    OrderInbounds_DetailQuery,
    OrderInbounds_DetailQueryVariables
  >(ORDER_INBOUNDS_DETAIL_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      ...itemQueryVariables,
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      mergeUUID: orderMetadata.mergeUUID,
      restaurantUUID: selectedRestaurantUUID,
      skipGroupMemberships: true,
      smartOrderUUID: orderMetadata.uuid,
      threadIds: orderMetadata.threadId,
    },
  });

  const realOrderMetadata: OrderMetadata[] = useMemo(() => {
    if (!data?.getSmartOrderDetails) {
      return [];
    }

    const seenUUIDs = new Set<string>();
    const deduplicatedOrders = data.getSmartOrderDetails.reduce<
      SmartOrderFieldsFragment[]
    >((acc, item) => {
      const order = item.smart_order;
      if (order && !seenUUIDs.has(order.uuid)) {
        seenUUIDs.add(order.uuid);
        acc.push(order);
      }

      return acc;
    }, []);

    const metadata = getOrderMetadata(deduplicatedOrders, {
      searchInputsMode: 'all',
    });

    return filterBySubmittedOrderUUID
      ? metadata.filter(
          (order) => order.submittedOrderUUID === filterBySubmittedOrderUUID,
        )
      : metadata;
  }, [data, filterBySubmittedOrderUUID]);

  if (loading) {
    return { data: null, error, loading: true };
  } else {
    return { data: realOrderMetadata, error, loading: false };
  }
};
